import { HttpClient } from "@angular/common/http";
import { Injector } from "@angular/core";
import { AreaHostBase } from "../area-host-base/area-host-base";
import { InfobarBase } from "../infobar-base/infobar-base";
import { TabConfig } from "../pollux-tab-config/pollux-form-config";
import { WorkplaceBase } from "../workplace-base/workplace-base";
import { AppServiceHelperService } from "../../../services/app-service-helper.service";
import { FileDownloadService } from "../../../services/file-download-service.service";
import { EventBusService } from "../../../services/event-bus.service";

/**
 * Standardized area host base class.
 */
export abstract class WorkplaceAreaHostBase extends AreaHostBase {
  /**
   * The infobar
   */
  public infobar: InfobarBase = null;

  /**
   * The current search tab visibility
   */
  public searchTabVisible = false;

  /**
   * Infobar visibility
   */
  public infoBarVisibility = false;

  /**
   * The selected import area
   */
  public selectedImportArea: string = null;

  /**
   * Export commands
   */
  public exportCommands: Map<string, any[]> = new Map<string, any[]>();

  /**
   * Selected tab hierarchy
   */
  public selectedTabHierarchy: number = null;

  /**
   * Selected tab hierarchy item type
   */
  public selectedTabHierarchyItemType: string = null;

  /**
   * Tab hierarchy item
   */
  public selectedTabHierarchyItem: any = null;

  /**
   * The workplace
   */
  public workplace: WorkplaceBase = null;

  /**
   * The current search tab
   */
  public searchTab = '';

  /**
   * Pane width configuration
   */
  public paneWidths: Map<string, number> = this.getPaneWidths();

  /**
   * Show the search bar
   */
  public showSearchBar = false;

  /**
   * Link counter object
   */
  public linkCounter: Map<string, number> = new Map<string, number>();

  /**
   * Event bus
   */
  protected eventBus: EventBusService;

  /**
   * HttpClient
   */
  protected http: HttpClient;

  /**
   * AppServiceHelperService
   */
  protected appServiceHelper: AppServiceHelperService;

  /**
   * File download service
   */
  protected fileDownloadService: FileDownloadService;

  /**
   * Constructor
   * @param injector Angular Injector
   */
  constructor(protected injector: Injector) {
    super();
    this.eventBus = injector.get(EventBusService);
    this.http = injector.get(HttpClient);
    this.appServiceHelper = injector.get(AppServiceHelperService);
    this.fileDownloadService = injector.get(FileDownloadService);
  }

  /**
   * Gets the default pane widths
   * @returns the default pane widths
   */
  public abstract getPaneWidths(): Map<string, number>;

  /**
   * Workplace creation callback. Saves the reference to the workplace component
   * @param workplace The workplace component
   */
  public onWorkplaceCreated(workplace: WorkplaceBase): void {
    this.workplace = workplace;
  }

  /**
   * Sets a counter value
   * @param area The tab
   * @param amount The value
   */
  public setCounterValue(area: string, amount: number): void {
    this.linkCounter.set(area, amount);
    this.eventBus.broadcastEvent('setCounterValueBroadcast', area, amount);
  }

  /**
   * Gets the counter value for the specified tab
   * @param area The tab
   * @returns The amount of items in the tab
   */
  public getCounterValue(area: string): number {
    if (this.linkCounter.has(area)) {
      return this.linkCounter.get(area);
    }
    return 0;
  }

  /**
   * Sets the selected import area
   * @param area The area/tab
   */
  public setSelectedImportArea(area: string): void {
    this.selectedImportArea = area;

    this.exportCommands = new Map<string, any[]>();
  }


  /**
   * An element was selected in a tab
   * @param tabItem The tab
   * @param item The item
   */
  public onElementSelected(tabItem: TabConfig, item: any): void {
    if (this.infobar) {
      this.infobar.onElementSelected(tabItem, item);
    }

    if (!this.exportCommands.has(tabItem.exportAreaName)) {
      this.exportCommands.set(tabItem.exportAreaName, []);
    }

    try {
      this.exportCommands.set(tabItem.exportAreaName, []);

      if (item.length > 0) {
        for (const currentItem of item) {
          if (currentItem.Oid) {
            this.exportCommands.get(tabItem.exportAreaName).push(currentItem.Oid);
          }
          else if (currentItem.oid) {
            this.exportCommands.get(tabItem.exportAreaName).push(currentItem.oid);
          }
        }
      }
      else if (item.Oid) {
        this.exportCommands.get(tabItem.exportAreaName).push(item.Oid);
      }
      else if (item.oid) {
        this.exportCommands.get(tabItem.exportAreaName).push(item.oid);
      }
    } catch (e) {
      //Nothing
    }
  }

  /**
   * An element was deselected in the tab
   * @param tabItem The tab
   */
  public onElementDeselected(tabItem: TabConfig): void {
    this.exportCommands.set(tabItem.exportAreaName, []);
  }

  /**
   * Performs an export
   */
  public doExport(): void {
    this.workplace.doExport();
  }

  /**
   * Creates a template
   */
  public createTemplate(): void{
    if (!this.selectedImportArea) {
      this.eventBus.broadcastEvent("openConfirmDialog", {
        success: () => {
          //Nothing
        }
      }, 'noImportInThisSection');
      return;
    }

    const importAreas = [this.selectedImportArea];

    for (const importArea of importAreas) {
      const url = this.appServiceHelper.getServiceEndpoint('pollux.import.api', `import/template?form=${importArea}`);
      this.fileDownloadService.initiateDownload(url, null, null);
    }
  }

  /**
   * Performs an import. Not implemented in PD area
   */
  public doImport(): void{
    this.eventBus.broadcastEvent("openConfirmDialog", {
      success: () => {
        //Nothing
      }
    }, 'noImportInThisSection');
  }

  /**
   * Infobar creation callback. Saves the reference to the infobar component
   * @param infobar The infobar component
   */
  public onInfobarCreated(infobar: InfobarBase): void {
    this.infobar = infobar;
  }

  /**
   * Gets theinfobar visibility
   * @returns
   */
  public getInfoBarVisibility(): boolean {
    return this.infoBarVisibility;
  }

  /**
   * Sets the infobar visibility
   * @param mode Show (true) or hide
   * @param areaChanged Area changed?
   * @param enableSettings Enable settings
   * @param params Parameters
   */
  public abstract setInfoBarVisibility(mode: boolean, areaChanged: boolean, enableSettings: boolean, params: any): void;

  /**
   * Sets the search bar visibility
   * @param mode Show (true) or hide
   */
  public setSearchModeVisibility(mode: boolean): void {
    if (mode) {
      this.showSearchBar = true;
    }
    else {
      this.showSearchBar = false;
    }
  }

  /**
   * Resizes a pane
   * @param id The ID
   * @param width The width in px
   */
  public resizePane(id: string, width: number): void {
    if (this.paneWidths.has(id)) {
      this.paneWidths.set(id, width);
    }
    else {
      console.warn("Tried to resizePane for invalid id: " + id);
    }
  }
}
